import React, { useMemo } from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import { Row, Col, Card, CardBody, Badge, Button } from "reactstrap";
import SuiteLayout from "../../_suite/SuiteLayout";
import HealthcareDashboard from "../../healthcare";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import IntlMessages from "Util/IntlMessages";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import { AsyncStateBoundary, SuitePageShell } from "../../_suite/primitives";

function HealthcareDisclaimerStrip() {
  return (
    <Row className="mb-3">
      <Colxx xxs="12">
        <div className="alert alert-warning py-2 small mb-0">
          <IntlMessages id="health.disclaimer.title" />{" "}
          <IntlMessages id="health.disclaimer.body" />
        </div>
      </Colxx>
    </Row>
  );
}

function AppointmentsWeekGridPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.healthcare.listAppointments(),
    []
  );
  const slots = ["09:00", "10:30", "11:00"];
  const util = useMemo(() => {
    const list = data || [];
    const filled = list.filter(a => a.slotFilled).length;
    return { filled, total: list.length, pct: list.length ? Math.round((filled / list.length) * 100) : 0 };
  }, [data]);
  return (
    <div>
      <HealthcareDisclaimerStrip />
      <SuitePageShell heading={<IntlMessages id="suite.health.appointments" />}>
        <AsyncStateBoundary loading={loading} error={error}>
          <p className="small text-muted mb-3">
            Slot fill (demo): {util.filled}/{util.total} booked ({util.pct}%)
          </p>
          <Row>
            {slots.map(t => (
              <Col md="4" key={t} className="mb-3">
                <Card className="border-0 shadow-sm h-100">
                  <CardBody>
                    <div className="text-muted small mb-2">{t}</div>
                    {(data || [])
                      .filter(a => a.time === t)
                      .map(a => (
                        <div key={a.id} className="mb-2 p-2 bg-light rounded">
                          {a.patient ? (
                            <div className="small font-weight-bold">{a.patient}</div>
                          ) : (
                            <Badge color="success" className="mb-1">
                              Open slot
                            </Badge>
                          )}
                          <div className="small">{a.provider}</div>
                          {a.durationMin != null && (
                            <div className="small text-muted">{a.durationMin} min</div>
                          )}
                          <Button
                            tag={Link}
                            color="link"
                            size="sm"
                            className="p-0"
                            to={a.id}
                            relative="path"
                          >
                            Open
                          </Button>
                        </div>
                      ))}
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </AsyncStateBoundary>
      </SuitePageShell>
    </div>
  );
}

function AppointmentDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.healthcare.getAppointment(id),
    [id]
  );
  return (
    <div>
      <HealthcareDisclaimerStrip />
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.health.appointment-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Card>
            <CardBody>
              <h4>{data.time}</h4>
              <p>{data.patient}</p>
              <p>{data.provider}</p>
              <Badge>{data.type}</Badge>
            </CardBody>
          </Card>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function ClaimsPayerSplitPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.healthcare.listClaims(),
    []
  );
  return (
    <div>
      <HealthcareDisclaimerStrip />
      <SuitePageShell heading={<IntlMessages id="suite.health.claims" />}>
        <AsyncStateBoundary loading={loading} error={error}>
          <Row>
            {(data || []).map(c => (
              <Col md="6" key={c.id} className="mb-3">
                <Card className="border-0 shadow-sm h-100">
                  <CardBody>
                    <div className="d-flex justify-content-between">
                      <div>
                        <h5>{c.id}</h5>
                        <p className="text-muted small mb-0">{c.payer}</p>
                      </div>
                      <div className="text-end">
                        <div className="h4">${c.amount}</div>
                        <Badge>{c.status}</Badge>
                      </div>
                    </div>
                    {(c.steps || []).length > 0 && (
                      <ul className="small text-muted ps-3 mb-0 mt-2">
                        {c.steps.map((s, i) => (
                          <li key={i}>
                            {s.done ? "✓" : "○"} {s.label}
                          </li>
                        ))}
                      </ul>
                    )}
                    <Button
                      tag={Link}
                      color="outline-primary"
                      size="sm"
                      className="mt-2"
                      to={c.id}
                      relative="path"
                    >
                      Detail
                    </Button>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </AsyncStateBoundary>
      </SuitePageShell>
    </div>
  );
}

function ClaimDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.healthcare.getClaim(id),
    [id]
  );
  return (
    <div>
      <HealthcareDisclaimerStrip />
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.health.claim-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Card>
            <CardBody>
              <h4>{data.id}</h4>
              <p>Payer: {data.payer}</p>
              <p>Amount ${data.amount}</p>
              {(data.steps || []).length > 0 && (
                <ul className="small mb-0 ps-3">
                  {data.steps.map((s, i) => (
                    <li key={i}>
                      {s.done ? "✓" : "○"} {s.label}
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function ProvidersSchedulePage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.healthcare.listProviders(),
    []
  );
  return (
    <div>
      <HealthcareDisclaimerStrip />
      <SuitePageShell heading={<IntlMessages id="suite.health.providers" />}>
        <AsyncStateBoundary loading={loading} error={error}>
          {(data || []).map(p => (
            <Card key={p.id} className="mb-3 border-0 shadow-sm">
              <CardBody className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">{p.name}</h5>
                  <div className="small text-muted">{p.dept}</div>
                </div>
                <div className="text-end">
                  <div className="small">Utilization</div>
                  <div className="h4 mb-0">{p.utilPct}%</div>
                  {p.slotsOpen != null && (
                    <div className="small text-muted mt-1">
                      {p.slotsOpen} slots open this week
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          ))}
        </AsyncStateBoundary>
      </SuitePageShell>
    </div>
  );
}

export function HealthcareSuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="healthcare" />}>
        <Route index element={<HealthcareDashboard />} />
        <Route path="appointments" element={<AppointmentsWeekGridPage />} />
        <Route path="appointments/:id" element={<AppointmentDetailPage />} />
        <Route path="claims" element={<ClaimsPayerSplitPage />} />
        <Route path="claims/:id" element={<ClaimDetailPage />} />
        <Route path="providers" element={<ProvidersSchedulePage />} />
      </Route>
    </Routes>
  );
}
